import isNil from 'lodash/isNil';
import React, {
    useCallback, useEffect, useMemo, useState, FC, ChangeEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import Preloader from 'components/Preloader';
import { Status, StatusBox } from 'components/StatusBox';
import { Checkbox, Table, Title } from 'ui-library';
import { ICON_TYPE_INFOBOX } from 'ui-library/components/Icon';
import Icon from 'ui-library/components/Icon/Icon';
import Popover from 'ui-library/components/Popover';
import { adaptAllocations } from './adaptAllocations';
import { columnDefs, footerColumnDefs } from './columnDefs';

// --- Interfaces ---

interface Security {
    id: number;
    value: number;
    disabled?: boolean;
    checked?: boolean;
}

interface PortfolioItem {
    id: number;
    name: {
        value: string;
        checked: boolean;
    };
    allocation: {
        id: number;
        value: number;
        disabled: boolean;
    };
    [key: string]: any; // For other properties from columnDefs
}

interface PortfolioFooter {
    allocation?: number;
    [key: string]: any;
}

interface IndividualPortfolioData {
    body: PortfolioItem[];
    footer: PortfolioFooter;
}

interface ModelPortfolioDetails {
    Attributes?: string;
    Positions?: Array<{
        Allocation: number;
        Security: {
            Id: number;
            Name: string;
        };
    }>;
}

interface CachedStrategy {
    individualPortfolio?: Array<{
        securityId: number;
        allocation: number;
    }>;
    rebalancing?: boolean;
    modelPortfolioId?: string | number;
}

interface Props {
    individualPortfolioData?: IndividualPortfolioData;
    isLoading?: boolean;
    isRebalance?: boolean;
    setRebalance?: (checked: boolean) => void;
    modelPortfolioDetails?: ModelPortfolioDetails;
    setIndividualPortfolioData?: (data: any) => void;
    setValidationError?: (error: string | undefined | null) => void;
    cachedSelectStrategy?: CachedStrategy;
    setupWithdrawalPlan?: boolean;
}

// --- Component ---

const IndividualPortfolioSecurities: FC<Props> = ({
    individualPortfolioData = { body: [], footer: {} },
    isLoading = true,
    isRebalance = false,
    setRebalance = () => {},
    modelPortfolioDetails = {},
    setIndividualPortfolioData = () => {},
    setValidationError = () => {},
    cachedSelectStrategy,
    setupWithdrawalPlan,
}) => {
    const { t } = useTranslation();

    const isWithdrawalEnabled = useMemo(
        () => JSON.parse(modelPortfolioDetails?.Attributes || '{}')?.WithdrawalOption,
        [modelPortfolioDetails],
    );

    const onChangeRebalance = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setRebalance(e?.target?.checked);
    }, [setRebalance]);

    useEffect(() => () => {
        setValidationError(null);
    }, [setValidationError]);

    useEffect(() => {
        setValidationError(
            !isNil(individualPortfolioData?.footer?.allocation)
            && individualPortfolioData?.footer?.allocation !== 100
                ? t('portfolio.createNewPortfolio.strategy.individualPortfolio.error.OneHundred')
                : undefined,
        );
    }, [individualPortfolioData?.footer?.allocation, t, setValidationError]);

    const updateAllocationById = useCallback(({
        id,
        allocationUpdateObject = {},
        nameUpdateObject = {},
    }: {
        id: number | string;
        allocationUpdateObject?: Record<string, any>;
        nameUpdateObject?: Record<string, any>;
    }) => {
        setIndividualPortfolioData((prevData: any) => {
            if (!prevData?.body) return prevData;

            const newBody = prevData.body.map((item: any) => {
                if (item?.id !== id) return item;

                return {
                    ...item,
                    allocation: {
                        ...item.allocation,
                        ...allocationUpdateObject,
                    },
                    name: {
                        ...item.name,
                        ...nameUpdateObject,
                    },
                };
            });

            const newTotalAllocation = newBody.reduce((accu: number, item: any) => {
                const val = item?.name?.checked ? (item?.allocation?.value || 0) : 0;

                return accu + val;
            }, 0);

            return {
                ...prevData,
                body: newBody,
                footer: {
                    ...prevData.footer,
                    allocation: newTotalAllocation,
                },
            };
        });
    }, [setIndividualPortfolioData]);

    const onAllocationChange = useCallback((val: { id: number | string; value: number }) => {
        const { id, value } = val;

        updateAllocationById({
            id,
            allocationUpdateObject: value === 0 ? { value, disabled: true } : { value },
            nameUpdateObject: value === 0 ? { checked: false } : {},
        });
    }, [updateAllocationById]);

    const onCheckBoxChange = useCallback((value: { id: number | string; checked: boolean }) => {
        const { id, checked } = value;

        updateAllocationById({
            id,
            allocationUpdateObject: { value: checked ? 10 : 0, disabled: !checked },
            nameUpdateObject: { checked },
        });
    }, [updateAllocationById]);

    useEffect(() => {
        const data: any = adaptAllocations({
            t, data: modelPortfolioDetails, onAllocationChange, onCheckBoxChange,
        });
        const cachedIndividualPortfolio = cachedSelectStrategy?.individualPortfolio;

        if (cachedIndividualPortfolio?.length) {
            data.body = data.body.map((item: PortfolioItem) => {
                const cachedAllocation = cachedIndividualPortfolio.find((cachedItem) => (item?.allocation?.id === cachedItem.securityId));

                if (cachedAllocation) {
                    return {
                        ...item,
                        name: { ...item?.name, checked: true },
                        allocation: {
                            ...item?.allocation,
                            value: cachedAllocation.allocation * 100,
                            disabled: false,
                        },
                    };
                }

                return {
                    ...item,
                    name: { ...item?.name, checked: false },
                    allocation: { ...item?.allocation, disabled: true },
                };
            });

            data.footer = {
                ...data.footer,
                allocation: Math.round(100 * cachedIndividualPortfolio.reduce((accu, item) => (item.allocation + accu), 0)),
            };
        }

        if (typeof cachedSelectStrategy?.rebalancing === 'boolean') {
            setRebalance(cachedSelectStrategy?.rebalancing);
        }

        setIndividualPortfolioData(data);
    }, [
        t,
        modelPortfolioDetails,
        cachedSelectStrategy,
        onAllocationChange,
        onCheckBoxChange,
        setIndividualPortfolioData,
        setRebalance,
    ]);

    useEffect(() => {
        if (!isWithdrawalEnabled && setupWithdrawalPlan) {
            setIndividualPortfolioData({ body: [], footer: {} });
        }
    }, [isWithdrawalEnabled, setupWithdrawalPlan, setIndividualPortfolioData]);

    return (
        <div className="fi-product-mark-selection">
            {!isWithdrawalEnabled && setupWithdrawalPlan ? (
                <StatusBox
                    status={Status.Warning}
                    description={t('portfolioDetail.strategy.withdrawalPlanNotSupportedForIndividual')}
                />
            ) : (
                <>
                    <Title type={2}>{t('portfolio.createNewPortfolio.allocations')}</Title>
                    <Preloader isLoading={isLoading}>
                        <div className="new-portfolio-content">
                            <Table
                                data={individualPortfolioData.body}
                                columns={columnDefs(t)}
                                footerColumns={footerColumnDefs(t)}
                                mini={false}
                                footer={individualPortfolioData.footer}
                            />

                            <div className="rebalance-container">
                                <Checkbox
                                    className="rebalance"
                                    label={t('portfolio.createNewPortfolio.allocations.rebalance')}
                                    onChange={onChangeRebalance}
                                    checked={isRebalance}
                                />
                                <Popover
                                    content={<div style={{ padding: '10px', maxWidth: '300px' }}>{t('portfolioDetail.strategy.rebalancingTooltip')}</div>}
                                    trigger="hover"
                                >
                                    <Icon type={ICON_TYPE_INFOBOX} className="search-icon" size={16} />
                                </Popover>
                            </div>
                        </div>
                    </Preloader>
                </>
            )}
        </div>
    );
};

export default IndividualPortfolioSecurities;
